import { useContext, useEffect, useState } from 'react';
import { BootstrapContext } from 'app/bootstrap/BootstrapContext';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { useDispatch, useSelector } from 'react-redux';
import history from '@history';
import WdPageSimple from '@main/core/WdPageSimple';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { CircularProgress, Grid, Switch, Typography } from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import {
  BasicSelect,
  ButtonWithExpand,
  ComposedHeader,
  ComposedTable,
  InputSearch,
  Paginator,
} from '@main/components';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { deleteAdmin, exportFile, searchAdmins, statusAdmin } from 'app/api/admin';
import { useSimpleAlert } from '@main/hooks/useSimpleAlert';
import { showMessage } from 'app/store/core/messageSlice';
import { useNativeDebounce, useRequest } from '@main/hooks';
import { formatDate } from 'app/utils/dates';
import _ from 'lodash';
import { useDownloadBlob } from '@main/hooks/useDownloadBlob';
import { customSwitchStyles } from 'app/styles/styled/customSwitchStyles';
import { useTheme } from '@mui/styles';
import { hasScope } from 'app/scopes/scopes';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { defaultsEnum } from 'src/app/enums/defaultsEnum';
import { typeDownloadEnum } from 'src/app/enums/fileEnum';
import { statusAdminsData, tableAdminsScheme } from '../Defaults/admins';
import AdminsOptions from '../Components/AdminsOptions/AdminsOptions';

const exportFileOptions = _.omit(typeDownloadEnum, ['txt', 'zip']);

const Root = styled(WdPageSimple)(({ theme }) => ({
  '& .WdPageSimple-header': {},
  '& .WdPageSimple-content': {},
}));

const Admins = () => {
  const {
    id: selfId,
    email: selfEmail,
    scopes: selfScopes,
  } = useSelector((state) => state.profile);
  const { config } = useSelector((state) => state.config);
  const { api } = config;
  const Dispatch = useDispatch();
  const { finalizeSession } = useContext(BootstrapContext);
  const openAlert = useSimpleAlert();
  const theme = useTheme();

  // ------ States base correspondientes a logica de filtrado -------
  const [dataList, setDataList] = useState([]);
  const [querySearch, setQuerySearch] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(25);
  const [datePickerValue, setDatePickerValue] = useState([null, null]);
  // ------ State general de formulario de bsuqueda ------------
  const [form, setForm] = useState({ query: '', initDate: '', endDate: '', paginate: undefined });

  // ------- Selects extras de busqueda --------------
  const [statusSelect, setStatusSelect] = useState('');

  // ----- Instancia de request que se usaran en la vista junto a sus valores de estado ---------
  const handleOnForbidden = async () => {
    await finalizeSession();
  };

  const handleOnError = async (message) => {
    Dispatch(showMessage({ message }));
  };

  const {
    makeRequest: trySearch,
    isLoading: isLoadingSearch,
    response: responseSearch,
  } = useRequest(searchAdmins, {
    onForbidden: handleOnForbidden,
    onError: handleOnError,
  });
  const { makeRequest: tryDisableAdmin } = useRequest(deleteAdmin, {
    onError: handleOnError,
  });
  const { makeRequest: tryUpdateUserStatus } = useRequest(statusAdmin, {
    onError: handleOnError,
  });

  // ---- Amortiguador de peticiones
  const { triggerAction } = useNativeDebounce({ delay: 600 });

  // ----- Instancia para exportacion de archivos -----
  const {
    formats,
    tryDownload,
    progress: progressExport,
    isLoading: loadingExport,
  } = useDownloadBlob(exportFileOptions, exportFile);

  const handleExportFile = async (format) => {
    await tryDownload({
      type: format,
      name: 'Empleados',
    });
  };

  // ----- Handler para disparar peticion amortiguada
  const handleRequestDebounce = () => {
    triggerAction().then((isOk) => {
      if (!isOk) return;
      trySearch(form, currentPage);
    });
  };

  // ---- Borra todos los filtros
  const handleClearAllFilters = () => {
    setQuerySearch('');
    setCurrentItemsPerPage(25);
    setDatePickerValue([null, null]);
    setCurrentPage(1);
    // -------- Selects extras ------------
    setStatusSelect('');
  };

  // ------ Funciones para actualizar los filtros ------
  const handleSearchQuery = (query = '') => {
    if (!query.length > 3) return;
    setQuerySearch(query);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangeItems = (items) => {
    setCurrentItemsPerPage(items);
    setCurrentPage(1);
  };

  // ----- Actualiza el formulario acorde cambio de filtros
  useEffect(() => {
    if (
      querySearch === undefined ||
      currentItemsPerPage === undefined ||
      datePickerValue === undefined
    )
      return;
    setForm((current) => {
      return {
        ...current,
        query: querySearch,
        paginate: currentItemsPerPage,
        initDate: datePickerValue[0] === null ? '' : datePickerValue[0],
        endDate: datePickerValue[1] === null ? '' : datePickerValue[1],
        // ------ Selects extras -------
        status: statusSelect === '' ? null : statusSelect.toString(),
      };
    });
  }, [querySearch, currentItemsPerPage, datePickerValue, statusSelect]);

  // ------ Actualiza la informacion de la tabla despues de la peticion -------
  useEffect(() => {
    if (!responseSearch) return;
    setDataList(responseSearch.data);
    setTotalPages(responseSearch.last_page);
    setTotalItems(responseSearch.total);
    setCurrentPage(responseSearch.current_page);
  }, [responseSearch]);

  // -------- Realiza la peticion de datos al haber un cambio en el formulario -----
  useEffect(() => {
    if (form === undefined || form.paginate === undefined) return;
    console.log('FROM | QUERY => ', form);
    handleRequestDebounce();
  }, [currentPage, form]);

  // ------ Inicializacion de la vista con toda la informacion disponible -------
  useEffect(() => {
    return () => {
      setDataList([]);
    };
  }, []);

  /* ----- Handlers extras ---------- */
  const handleCreateAdmin = () => {
    history.push('/admin/create');
  };

  const handleDisable = async (id, name) => {
    if (!hasScope(selfScopes, 'Admins', 'delete')) return;

    openAlert({
      title: 'Atención',
      description: `¿Está seguro que desea deshabilitar al usuario ${name}?`,
    })
      .then((userResponse) => {
        if (!userResponse) {
          return;
        }
        tryDisableAdmin(id).then(({ isOk }) => {
          if (isOk) {
            Dispatch(
              showMessage({
                message: 'Administrador eliminado correctamente',
              })
            );
            handleRequestDebounce();
          }
        });
      })
      .catch((error) => console.log(error));
  };

  const handleUpdate = (id) => {
    if (!hasScope(selfScopes, 'Admins', 'update')) return;

    history.push(`/admin/update/${id}`);
  };

  const handleProfile = (currentId) => {
    const isMyself = currentId === selfId;
    const profilePath = isMyself ? '/myself' : `/admin/profile/${currentId}`;
    history.push(profilePath);
  };

  const handleUpdateUserStatus = async (id, status) => {
    tryUpdateUserStatus(id, { status: !status })
      .then(({ isOk }) => {
        if (isOk) {
          Dispatch(
            showMessage({
              message: 'Registro actualizado correctamente',
            })
          );
          handleRequestDebounce();
        }
      })
      .catch((error) => {
        Dispatch(
          showMessage({
            message: 'Error al actualizar el registro',
          })
        );
      });
  };

  return (
    <Root
      header={
        <ComposedHeader
          title="Administradores"
          description="Información general de accesos autorizados"
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-10 justify-end">
            {hasScope(selfScopes, 'Admins', 'store') && (
              <Button
                className="whitespace-nowrap mx-4"
                variant="contained"
                color="primary"
                onClick={() => handleCreateAdmin()}
                startIcon={<AdminPanelSettingsOutlinedIcon />}
              >
                Agregar administrador
              </Button>
            )}
            <ButtonWithExpand
              title={!loadingExport ? 'Descargar' : `Descargando ${progressExport}%`}
              className="whitespace-nowrap mx-4"
              variant="contained"
              color="success"
              startIcon={
                loadingExport ? (
                  <CircularProgress
                    color="inherit"
                    size="1.8rem"
                    variant={progressExport > 1 ? 'determinate' : 'indeterminate'}
                    value={progressExport}
                  />
                ) : (
                  <SimCardDownloadOutlinedIcon />
                )
              }
            >
              {formats.map((option, index) => (
                <ButtonWithExpand.Item
                  disabled={loadingExport}
                  key={`${index}_${option.value}_doc`}
                  text={`Documento ${option.label}`}
                  onClick={() => handleExportFile(option.value)}
                />
              ))}
            </ButtonWithExpand>
          </div>
        </ComposedHeader>
      }
      content={
        <Grid container className="px-8 sm:px-32 pb-32 pt-0 flex flex-row w-full justify-center">
          <Grid item xs={12} md={12} className="flex w-full">
            <Card className="pb-24 px-0 sm:px-24 mt-16 w-full">
              <CardContent className="flex w-full h-full flex-col justify-between">
                <div className="flex flex-col mt-24">
                  <Grid container className="w-full flex justify-center items-center py-8 mb-28">
                    <Grid item xs={12} md={7}>
                      <div className="flex flex-row flex-wrap sm:flex-nowrap justify-center sm:justify-start items-center">
                        <div className="ml-4 mr-8 my-2 w-full sm:w-8/12 md:my-0">
                          <InputSearch
                            value={querySearch}
                            onChange={(query) => handleSearchQuery(query)}
                          />
                        </div>
                        <div className="mx-4 my-2 md:my-0">
                          <Button
                            fullWidth
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={() => handleClearAllFilters()}
                            startIcon={<DeleteOutlineOutlinedIcon />}
                          >
                            Limpiar filtros
                          </Button>
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <div className="flex flex-row justify-center md:justify-end items-center md:mt-0 mt-8">
                        <DateRangePicker
                          slotProps={{ textField: { size: 'small' } }}
                          localeText={{
                            start: 'Fecha inicial',
                            end: 'Fecha final',
                          }}
                          value={datePickerValue}
                          onChange={(current) => setDatePickerValue(current)}
                          disableFuture
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} className="p-2">
                    <Grid item xs={12} md={7} className="p-8 mb-24">
                      <div className="flex flex-row flex-wrap justify-center sm:justify-start items-center">
                        <div className="px-4">
                          <BasicSelect
                            data={statusAdminsData}
                            onChange={(data) => setStatusSelect(data)}
                            title="Estatus"
                            value={statusSelect}
                            size="small"
                          />
                        </div>
                        {/*
                          Agrega mas selects
                        */}
                      </div>
                    </Grid>
                    <Grid item xs={12} md={5} className="p-8 mb-24">
                      <div className="flex flex-row justify-end">
                        <Paginator
                          totalPages={totalPages}
                          totalItems={totalItems}
                          onChangeCurrentPage={(page) => handleChangePage(page)}
                          onChangeItemsPerPage={(items) => handleChangeItems(items)}
                          currentPage={currentPage}
                          currentItemsPerPage={currentItemsPerPage}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <div className="flex flex-col justify-center">
                    <ComposedTable
                      isLoading={isLoadingSearch}
                      className="w-full"
                      headers={tableAdminsScheme}
                      data={dataList}
                    >
                      <ComposedTable.Column
                        content={({ id, index }) => (
                          <Tooltip title={id}>
                            <span>{index || defaultsEnum.labels.table}</span>
                          </Tooltip>
                        )}
                      />
                      <ComposedTable.Column
                        content={(item) => (
                          <ComposedTable.Thumbail
                            color={item.background}
                            name={item.name}
                            baseUri={api?.host}
                            path={_.has(item, 'avatar') ? item.avatar.fullPathThumb : undefined}
                            alt={`perfil de ${item.name}`}
                          />
                        )}
                      />
                      <ComposedTable.Column
                        content={({ id, name }) =>
                          id && name ? (
                            <Button
                              size="small"
                              className="font-normal transition-colors duration-100 ease-in-out hover:text-blue-500"
                              onClick={() => handleProfile(id)}
                            >
                              {name}
                            </Button>
                          ) : (
                            defaultsEnum.labels.table
                          )
                        }
                      />
                      <ComposedTable.Column
                        content={({ email }) => (
                          <Typography className="whitespace-nowrap">
                            {email || defaultsEnum.labels.table}
                          </Typography>
                        )}
                      />
                      <ComposedTable.Column
                        content={({ phone }) => phone || defaultsEnum.labels.table}
                      />
                      <ComposedTable.Column
                        content={({ id, status }) =>
                          id ? (
                            <Switch
                              checked={status}
                              inputProps={{
                                'aria-label': 'Estatus',
                              }}
                              sx={customSwitchStyles(theme)}
                              onChange={() => handleUpdateUserStatus(id, status)}
                              disabled={selfId === id || !hasScope(selfScopes, 'Admins', 'update')}
                            />
                          ) : (
                            defaultsEnum.labels.table
                          )
                        }
                      />
                      <ComposedTable.Column content={() => <ComposedTable.NestedButton />} />
                      <ComposedTable.Column
                        content={(item) =>
                          item.created_at ? formatDate(item.created_at) : defaultsEnum.labels.table
                        }
                      />
                      <ComposedTable.Column
                        fixedRight
                        content={(item) => (
                          <AdminsOptions
                            subjectScopes={selfScopes}
                            authEmail={selfEmail}
                            admin={item}
                            onDelete={handleDisable}
                            onUpdate={handleUpdate}
                          />
                        )}
                      />
                    </ComposedTable>
                  </div>
                </div>
                <Grid container spacing={1} className="p-2 mt-24 flex">
                  <Grid item xs={12} md={12} className="py-8">
                    <div className="flex flex-row justify-end">
                      <Paginator
                        totalPages={totalPages}
                        totalItems={totalItems}
                        onChangeCurrentPage={(page) => handleChangePage(page)}
                        onChangeItemsPerPage={(items) => handleChangeItems(items)}
                        currentPage={currentPage}
                        currentItemsPerPage={currentItemsPerPage}
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      }
    />
  );
};

export default Admins;

